import { useMemo } from "react";
import { Box, Chip, Skeleton, Typography, useMediaQuery, useTheme } from "@mui/material";
import TimelineList from "examples/Timeline/TimelineList";
import TimelineItem from "examples/Timeline/TimelineItem";
import TimelineItemSkeleton from "examples/Timeline/TimelineItem/TimelineItemSkeleton";
import { TITULO_COLOR_BITACORA } from "../constants/bitacoraCotizacion.constants";
import { normalizarFilasBitacoraCotizacion } from "../utils/bitacoraCotizacionNormalizar.utils";
import { agruparFilasBitacoraCotizacion } from "../utils/bitacoraCotizacionAgrupar.utils";
import type { BitacoraCotizacionTimelineProps } from "../types/bitacoraCotizacion.types";

export const BitacoraCotizacionTimeline = ({
    bitacora,
    procesando,
    folio,
    folioRequisicion,
}: BitacoraCotizacionTimelineProps) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const filas = useMemo(
        () => agruparFilasBitacoraCotizacion(normalizarFilasBitacoraCotizacion(bitacora ?? [])),
        [bitacora]
    );

    const posicion = (index: number): "left" | "right" =>
        isMobile ? "right" : index % 2 === 0 ? "left" : "right";

    if (procesando) {
        return (
            <>
                <Skeleton variant="text" width="45%" height={48} sx={{ mb: 1, mx: "auto" }} />
                <TimelineList title="">
                    {[0, 1, 2, 3].map((i) => (
                        <TimelineItemSkeleton key={i} position={posicion(i)} />
                    ))}
                </TimelineList>
            </>
        );
    }

    if (filas.length === 0) return null;

    return (
        <>
            <Typography
                variant="h4"
                color="primary"
                gutterBottom
                sx={{
                    color: TITULO_COLOR_BITACORA,
                    display: "block",
                    fontSize: { xs: "1.25rem", md: "2rem" },
                }}
            >
                Bitácora de la cotización {folio}
            </Typography>

            {folioRequisicion && (
                <Chip
                    label={`Requisición ${folioRequisicion}`}
                    sx={{ fontWeight: "bold", fontSize: 12, background: "#ebe1ae", mb: 1 }}
                />
            )}

            <TimelineList title="">
                {filas.map((fila, index) => {
                    const lado = posicion(index);
                    // Izquierda → texto a la izquierda · Derecha → texto a la derecha
                    // En móvil todo va del lado derecho, pero se lee mejor alineado a la izquierda
                    const alineacion: "left" | "right" = isMobile ? "left" : lado;
                    const aLaDerecha = alineacion === "right";

                    return (
                        <TimelineItem
                            key={fila.id}
                            color={fila.color}
                            icon={fila.icon}
                            title={fila.titulo}
                            dateTime={fila.fecha}
                            name={fila.nombreUsuario}
                            description={
                                <Box
                                    sx={{
                                        width: "100%",
                                        textAlign: alineacion,
                                        alignSelf: aLaDerecha ? "flex-end" : "flex-start",
                                    }}
                                >
                                    {fila.agrupada ? (
                                        <Box
                                            component="ul"
                                            sx={{
                                                m: 0,
                                                pl: aLaDerecha ? 0 : 2.5,
                                                pr: 0,
                                                listStylePosition: aLaDerecha ? "inside" : "outside",
                                            }}
                                        >
                                            {fila.detalles.map((detalle, i) => (
                                                <Box component="li" key={i} sx={{ mb: 0.5 }}>
                                                    {detalle}
                                                </Box>
                                            ))}
                                        </Box>
                                    ) : (
                                        fila.descripcion
                                    )}
                                </Box>
                            }
                            firstItem={fila.esPrimero}
                            lastItem={fila.esUltimo}
                            position={lado}
                        />
                    );
                })}
            </TimelineList>
        </>
    );
};